"use strict";

/**
 * Defines a "class" that sends logged DDUX events directly to the DDUX server.
 * The class offers the following methods required for any "Sender":
 *
 *    start
 *    stop
 *    isRunning
 *    sendEvent
 *
 * @copyright 2020-2021 The MathWorks, Inc.
 */
define(["dojo/_base/lang", "dojo/Deferred", "dojo/io-query", "mw-log/Log"], function (lang, Deferred, ioQuery, Log) {
  const apiKeys = {
    DESKTOP_MATLAB: 'd8c22ba3-fc0f-4beb-99fc-a109fe9abba9',
    MATLAB_ONLINE: '86d464a0-b0c4-4c8d-a2bd-fa21d43f91f9',
    PRODUCT_INSTALLER_V2: '86aa5b7b-e4b6-4b3f-9226-7d816bea98aa',
    DESKTOP_MSH: '6a221899-611e-4316-ac1c-fbe5ea3a73e6',
    DOC_FEEDBACK: '5bbcb96c-9bb4-4aa2-8ac5-e9f6901ce151',
    CLOUD_CENTER: 'fdefc29f-b188-499a-890d-30a4f7e20292'
  };

  var _started;

  var _httpProperties;

  var _sessionKey;

  var _postURL = '';

  var cleanUpEventType = function cleanUpEventType(eventType) {
    if (!eventType || eventType === "click") {
      return 'clicked';
    }

    return eventType.replaceAll('_', ' ');
  };

  var cleanUpElementType = function cleanUpElementType(elementType) {
    if (!elementType) {
      return 'none';
    }

    return elementType.replaceAll('_', ' ');
  };

  var getIntegNumberForRelease = function getIntegNumberForRelease(release) {
    let indexToIntegMap = [1, 3, 2];

    if (release && release.length === 6) {
      let year = parseInt(release.substring(1, 5));
      let aorb = release.charAt(5);
      let index = ((year - 2016) * 2 + (aorb === 'a' ? 0 : 1)) % 3;
      return indexToIntegMap[index];
    }
  };

  var generateSessionKey = function generateSessionKey() {
    // This code is copied from mw-utils/Utils.generateUuid which only generates 8 characters.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      /*jslint bitwise: false */
      var r = Math.random() * 16 | 0,
          v = c === 'x' ? r : r & 0x3 | 0x8;
      /*jslint bitwise: true */

      return v.toString(16);
    });
  };

  return {
    /**
     * Initializes by storing relevant properties
     *
     * @param properties {object} supplies information needed to communicate with the server
     *        and optional event bundling control parameters
     * @param properties.applicationName the name of the application logging data (required)
     * @param properties.applicationVersion the version of the application loggig data (required)
     * @param properties.sendToDev specifies whether logged data should be send to the devevelopment (dev)
     *        or production (prod) URL. If not specified data goes to prod if the page hostname is "mathworks.com"
     *        (optional)
     *
     * @instance
     */
    start: function start(properties) {
      if (_started) {
        return;
      }

      _started = true;
      Log.assert(properties.applicationName, "applicationName must be supplied for direct send");
      Log.assert(properties.applicationVersion, "applicationVersion must be supplied for direct send");
      _httpProperties = {
        applicationName: properties.applicationName,
        applicationVersion: properties.applicationVersion,
        applicationKey: properties.applicationKey
      };
      _sessionKey = generateSessionKey(); // See if the page URL specifies a DDUX end point override

      let url = location.href;
      let queryIndex = url.indexOf("?");

      if (queryIndex >= 0) {
        let queryParameters = ioQuery.queryToObject(url.substring(queryIndex + 1, url.length));

        if (queryParameters.dduxUrlOverride) {
          _postURL = queryParameters.dduxUrlOverride;
        }
      }

      if (!_postURL) {
        _postURL = 'https://udc-service';

        if (properties.sendToDev || properties.sendToDev === undefined && location.hostname !== "mathworks.com") {
          let integNumber = properties.endPointNumber || getIntegNumberForRelease(properties.release) || 1;
          _postURL += '-integ' + integNumber;
        }

        _postURL += '.mathworks.com/udc/service/v1/events';
      }
    },

    /**
     * Terminates communication with the backend MATLAB
     *
     * @instance
     */
    stop: function stop() {
      if (_started) {
        _started = false;
      }
    },

    /**
     * Indicates whether the sender has been started and not stopped
     *
     * @returns {boolean} true if start, but not stop has been called.
     *
     * @instance
     */
    isRunning: function isRunning() {
      return _started;
    },

    /**
     * Sends an event to the server, either immediately or when bundle send is triggered
     *
     * @param eventType "telemetry", "uiEvent" or "composition"
     * @param event the attributes that define the event
     * @param obtainResult true if status should be obtained from the backend
     * @returns a promise if obtainResult true was specified, otherwise undefined
     */
    sendEvent: function sendEvent(eventType, event, obtainResult) {
      var eventData;
      var eventKey;

      if (eventType === 'telemetry') {
        /* Example telemetry event data
          "eventData": {
            "logDDUXData": {
              "product": "ML",
              "keyValues": {
                "activityState": "active",
                "activityType": "UI",
                "previousState": "idle",
                "transitionTime": "2020-12-24T13:32:41.876"
              }
            }
          }
        */
        eventData = {
          logDDUXData: {
            product: event.product,
            keyValues: event.data
          }
        };
        eventKey = event.eventKey;
      } else if (eventType === 'uiEvent') {
        /* Example UI event data
           "eventData": {
             "uiEvent": {
               "productName": "MATLAB",
               "uiScope": "App Designer",
               "elementId": "appDesignerToolstrip.layoutTab.tab",
               "elementType": "toolstrip tab",
               "eventType": "clicked",
               "values": {
                 "entry": [
                   {"key": "subscope",
                    "value": "toolstrip"
                   }
                 ]
               }
             }
        */
        eventData = {
          uiEvent: {
            productName: event.product,
            uiScope: event.scope,
            elementId: event.elementId,
            elementType: cleanUpElementType(event.elementType),
            eventType: cleanUpEventType(event.eventType)
          }
        };
        eventKey = 'MATLAB_DDUX_UI_EVENT'; // Find and store any key-value pairs other than the above standard pairs

        var keyValuePairs = [];

        for ([key, value] of Object.entries(event)) {
          if (key !== 'product' && key !== 'scope' && key !== 'elementId' && key !== 'elementType' && key !== 'eventType') {
            if (key === 'subScope') {
              key = "subscope";
            }

            keyValuePairs.push({
              'key': key,
              'value': value
            });
          }
        }

        if (keyValuePairs.length > 0) {
          eventData['values'] = {
            entry: keyValuePairs
          };
        }
      } else {
        Log.warn('Direct send to server not yet supported for ' + eventType + ' events');
        return;
      }

      var eventDataString = JSON.stringify(eventData);
      let request = new XMLHttpRequest();
      request.open('POST', _postURL);
      request.setRequestHeader('Content-type', 'application/json');
      request.setRequestHeader('x-mw-udc-client-version', '1.0');
      request.setRequestHeader('x-mw-udc-application-name', _httpProperties.applicationName);
      request.setRequestHeader('x-mw-udc-application-version', _httpProperties.applicationVersion);
      request.setRequestHeader('x-mw-authentication', _httpProperties.applicationKey || apiKeys[_httpProperties.applicationName]);
      let eventEntry = {
        sessionKey: _sessionKey,
        eventKey: eventKey,
        eventDate: new Date().toISOString().slice(0, 23),
        // Slice off trailing 'Z'
        eventData: eventDataString
      };

      if (event.appComponent) {
        eventEntry.applicationComponent = event.appComponent;
      }

      let message = {
        Event: [eventEntry]
      };
      let deferred;

      if (obtainResult) {
        deferred = new Deferred();
      }

      request.addEventListener("error", function () {
        Log.warn("DDUX http request failed");

        if (deferred) {
          deferred.resolve(6);
        }
      });
      request.addEventListener("load", function () {
        Log.info("DDUX http request succeeded");

        if (deferred) {
          deferred.resolve(0);
        }
      });
      request.send(JSON.stringify(message));

      if (deferred) {
        return deferred.promise;
      }
    }
  };
});
//# sourceMappingURL=DirectSender.js.map
